import {
  ConflictLevelsStatusEnum,
  type TaskFilterOptionDto,
} from '@sr-sdks/api-sdk-axios';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MAX_LIST_VALUES, isClientIdValue } from '../Title/titleFilterOptions';
import { useFilterParam } from '../Title/useTitleFilters';
import {
  TASK_FILTER_PARAMS,
  fitClaimantValues,
  isCalendarDateValue,
  isOtherClaimantValue,
  isTaskStatusValue,
  mergeOtherClaimantOptions,
  toMergedClaimantFilter,
} from './taskFilterOptions';
import { useWriteTaskParams } from './useWriteTaskParams';

const toQueryList = <T>(values: T[]) => (values.length ? values : undefined);

/** Read in place of the URL's params when the Client filter isn't offered. */
const NO_PARAMS = new URLSearchParams();

type UseTaskFiltersParams = {
  /** Agent workspaces only. Otherwise a `tasksClient` param has no control to clear it, so it is ignored. */
  isClientFilterAvailable: boolean;
  /** The filter options' other claimants, whose same-name keys show and filter as one. */
  otherClaimantOptions?: TaskFilterOptionDto[];
};

/**
 * Owns the Tasks-page filter state that lives in the URL. Each tab is its own
 * route, so its filters stay independent, and refresh or back/forward reproduces
 * the same result set.
 */
export const useTaskFilters = ({
  isClientFilterAvailable,
  otherClaimantOptions,
}: UseTaskFiltersParams) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const writeParams = useWriteTaskParams();

  const setFilterParam = useCallback(
    (key: string, value: string) => writeParams({ [key]: value }),
    [writeParams],
  );

  const status = useFilterParam(
    searchParams,
    setFilterParam,
    TASK_FILTER_PARAMS.status,
    isTaskStatusValue,
  );

  const dueDate = useFilterParam(
    searchParams,
    setFilterParam,
    TASK_FILTER_PARAMS.dueDate,
    isCalendarDateValue,
    MAX_LIST_VALUES,
  );

  const claimantKeys = useFilterParam(
    searchParams,
    setFilterParam,
    TASK_FILTER_PARAMS.otherClaimant,
    isOtherClaimantValue,
    MAX_LIST_VALUES,
  );

  const mergedClaimants = useMemo(
    () => mergeOtherClaimantOptions(otherClaimantOptions),
    [otherClaimantOptions],
  );

  const otherClaimant = toMergedClaimantFilter(
    claimantKeys,
    mergedClaimants.keysByValue,
  );

  // A ticked group filters by all its keys, including keys added since, within the cap.
  const otherClaimantKeys = fitClaimantValues(
    otherClaimant.selected,
    mergedClaimants.keysByValue,
  ).keys;

  const keptClaimantKeys = claimantKeys.selected
    .filter((key) => otherClaimantKeys.includes(key))
    .join(',');
  const hasUnfittedClaimantKeys =
    keptClaimantKeys !== claimantKeys.selected.join(',');

  /*
   * A group past the cap is unticked and unrequested, so its keys leave the URL too.
   * The list doesn't change, so the page stays and no history entry is added.
   */
  useEffect(() => {
    if (!hasUnfittedClaimantKeys) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (keptClaimantKeys) {
          next.set(TASK_FILTER_PARAMS.otherClaimant, keptClaimantKeys);
        } else {
          next.delete(TASK_FILTER_PARAMS.otherClaimant);
        }

        return next;
      },
      { replace: true },
    );
  }, [hasUnfittedClaimantKeys, keptClaimantKeys, setSearchParams]);

  const client = useFilterParam(
    isClientFilterAvailable ? searchParams : NO_PARAMS,
    setFilterParam,
    TASK_FILTER_PARAMS.client,
    isClientIdValue,
    MAX_LIST_VALUES,
  );

  const clearFilters = useCallback(
    () =>
      writeParams(
        Object.fromEntries(
          Object.values(TASK_FILTER_PARAMS).map((param) => [param, '']),
        ),
      ),
    [writeParams],
  );

  const hasFilterSelection = [status, dueDate, otherClaimant, client].some(
    (filter) => filter.selected.length > 0,
  );

  const queryFilters = {
    clientIds: toQueryList(client.selected),
    dueDates: toQueryList(dueDate.selected),
    // Resolved claims are completed tasks, which the list leaves out unless
    // asked. Not read off `statuses`, which the count badges also send (APP-2577).
    includeResolved:
      status.selected.includes(ConflictLevelsStatusEnum.Resolved) || undefined,
    otherClaimants: toQueryList(otherClaimantKeys),
    statuses: toQueryList(status.selected),
  };

  return {
    clearFilters,
    client,
    dueDate,
    hasFilterSelection,
    otherClaimant,
    otherClaimantOptions: mergedClaimants.options,
    queryFilters,
    status,
  };
};

export type TaskFilters = ReturnType<typeof useTaskFilters>;
